import React, { useState, useEffect } from 'react';
import { Line, Bar } from 'react-chartjs-2';
import { formatCurrency } from '../../utils/currencyFormatter';
import VideoStatistics from './VideoStatistics';
import BucketActivity from './BucketActivity';
import BucketStatistics from './BucketStatistics';
import DeletedBucketAnalysis from './DeletedBucketAnalysis';
import api from '../../utils/ApiManager';

const RevenueOverview = ({ salesData, bucketStats, userActivity }) => {
    const [currency, setCurrency] = useState('USD');

    useEffect(() => {
        const fetchPricing = async () => {
            try {
                const response = await api.get('/api/pricing');
                setCurrency(response.data.data.currency);
            } catch (error) {
                console.error('Error fetching pricing:', error);
            }
        };
        fetchPricing();
    }, []);

    const renderRevenueChart = () => {
        if (!salesData?.dailyStats) return null;

        const data = {
            labels: Object.keys(salesData.dailyStats),
            datasets: [{
                label: 'Daily Revenue',
                data: Object.values(salesData.dailyStats),
                borderColor: '#6D9773',
                backgroundColor: 'rgba(109, 151, 115, 0.1)',
                fill: true,
            }]
        };

        return (
            <Line 
                data={data}
                options={{
                    responsive: true,
                    plugins: {
                        legend: { position: 'top' },
                        title: { display: false }
                    }
                }}
            />
        );
    };

    const renderActivityChart = () => {
        if (!userActivity?.daily) return null;

        const data = {
            labels: Object.keys(userActivity.daily),
            datasets: [{
                label: 'User Activity',
                data: Object.values(userActivity.daily),
                backgroundColor: '#6D9773',
            }]
        };

        return (
            <Bar
                data={data}
                options={{
                    responsive: true,
                    plugins: {
                        legend: { position: 'top' },
                        title: { display: false }
                    }
                }}
            />
        );
    };

    // Calculate conversion rate
    const conversionRate = bucketStats?.newBucketsThisMonth && bucketStats?.completedBucketsThisMonth
        ? ((bucketStats.completedBucketsThisMonth / bucketStats.newBucketsThisMonth) * 100).toFixed(1)
        : '0.0';

    return (
        <div className="space-y-6">
            {/* First Row: Revenue Overview (1x) and Revenue Trend (2x) */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Revenue Overview */}
                <div className="bg-white border border-[#6D9773]/20 rounded-lg shadow-sm p-6 text-[#0C3B2E]">
                    <h3 className="text-lg font-medium text-[#6D9773] mb-4">Revenue Overview</h3>
                    <div className="space-y-4">
                        <div>
                            <p className="text-sm font-medium text-gray-600">All Time</p>
                            <p className="text-2xl font-semibold">
                                {formatCurrency(salesData?.totalRevenue || 0, currency)}
                            </p>
                            <p className="text-xs text-gray-500">Total revenue generated</p>
                        </div>
                        <div>
                            <p className="text-sm font-medium text-gray-600">Last 30 Days</p>
                            <p className="text-2xl font-semibold">
                                {formatCurrency(
                                    Object.values(salesData?.dailyStats || {}).reduce((sum, val) => sum + val, 0),
                                    currency
                                )}
                            </p>
                            <p className="text-xs text-gray-500">Recent revenue</p>
                        </div>
                        <div>
                            <p className="text-sm font-medium text-gray-600">Per Image Metrics</p>
                            <p className="text-xl font-semibold">
                                {formatCurrency(bucketStats?.avgRevenuePerImage || 0, currency)}
                            </p>
                            <p className="text-xs text-gray-500">Average revenue per image</p>
                        </div>
                        <div>
                            <p className="text-sm font-medium text-gray-600">Conversion Rate</p>
                            <p className="text-xl font-semibold">{conversionRate}%</p>
                            <p className="text-xs text-gray-500">Of new buckets completed</p>
                        </div>
                    </div>
                </div>

                {/* Revenue Trend (spans 2 columns) */}
                <div className="lg:col-span-2 bg-white border border-[#6D9773]/20 rounded-lg shadow-sm p-6 text-[#0C3B2E]">
                    <h3 className="text-lg font-medium text-[#6D9773] mb-4">Revenue Trend</h3>
                    <div className="h-64">
                        {renderRevenueChart()}
                    </div>
                </div>
            </div>

            {/* Second Row: Revenue Analysis (1x) and Bucket Statistics (2x) */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Revenue Analysis */}
                <div className="bg-white border border-[#6D9773]/20 rounded-lg shadow-sm p-6 text-[#0C3B2E]">
                    <h3 className="text-lg font-medium text-[#6D9773] mb-4">Revenue Analysis</h3>
                    <div className="space-y-4">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Revenue per Bucket</p>
                            <p className="text-2xl font-semibold">
                                {formatCurrency(bucketStats?.avgRevenue || 0, currency)}
                            </p>
                            <p className="text-xs text-gray-500">Average revenue per bucket</p>
                        </div>
                        <div>
                            <p className="text-sm font-medium text-gray-600">Monthly Growth</p>
                            <p className="text-xl font-semibold">
                                {((bucketStats?.last30Days?.avgRevenue / bucketStats?.avgRevenue - 1) * 100).toFixed(1) || '0.0'}%
                            </p>
                            <p className="text-xs text-gray-500">Compared to all-time average</p>
                        </div>
                    </div>
                </div>

                {/* Bucket Statistics (spans 2 columns) */}
                <div className="lg:col-span-2">
                    <BucketStatistics bucketStats={bucketStats} currency={currency} />
                </div>
            </div>

            {/* Third Row: Bucket Activity, Deleted Buckets Analysis, and Video Statistics */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <BucketActivity bucketStats={bucketStats} />
                <DeletedBucketAnalysis bucketStats={bucketStats} currency={currency} />
                <VideoStatistics bucketStats={bucketStats} />
            </div>

            {/* Fourth Row: User Activity (2x) and Processing Analysis (1x) */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* User Activity */}
                <div className="lg:col-span-2 bg-white border border-[#6D9773]/20 rounded-lg shadow-sm p-6 text-[#0C3B2E]">
                    <h3 className="text-lg font-medium text-[#6D9773] mb-4">User Activity</h3>
                    <div className="h-64">
                        {renderActivityChart()}
                    </div>
                </div>

                {/* Processing Analysis */}
                <div className="bg-white border border-[#6D9773]/20 rounded-lg shadow-sm p-6 text-[#0C3B2E]">
                    <h3 className="text-lg font-medium text-[#6D9773] mb-4">Processing Analysis</h3>
                    <div className="space-y-4">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Average Processing Time</p>
                            <p className="text-2xl font-semibold">{bucketStats?.allTime?.avgProcessingTime || '0'}s</p>
                            <p className="text-xs text-gray-500">Per bucket processing</p>
                        </div>
                        <div>
                            <p className="text-sm font-medium text-gray-600">Success Rate</p>
                            <p className="text-xl font-semibold">
                                {bucketStats?.allTime?.successRate?.toFixed(1) || '0.0'}%
                            </p>
                            <p className="text-xs text-gray-500">Video generation success rate</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RevenueOverview;